import React, {useCallback, useEffect, useState} from "react";
import {db} from '../../../firebase/FirebaseProvider'
import {
    doc,
    collection,
    getDoc,
    getDocs,
    query,
    where,
    orderBy,
    Timestamp,
} from 'firebase/firestore'
import {useParams} from 'react-router-dom';
import {Location, User} from "../../types/Types";
import {Card, ListGroup} from "react-bootstrap";
import UserDetails from "../UserDetails";
import UserDetailsV2 from "../UserDetailsV2";
import UserNav from "../UserNav";
import PageTracking from "../../auth/PageTracking";
import {Link} from "react-router-dom";
import DateTime from "../../date/DateTime";
import CameraTag from "../../tag/CameraTag";
import CommentTag from "../../tag/CommentTag";
import {V2GlassPanel, V2GlassSurface} from "../../shared/v2/V2Glass";
import {V2GlassChip} from "../../shared/v2/V2Glass";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCamera, faComment, faLocationDot} from "@fortawesome/free-solid-svg-icons";
import {getPhotoDate} from "../../util/Util";

interface Like {
    id: string;
    userId: string;
    userName: string;
    likeType: string;
    entityId: string;
    locationId: string;
    locationName: string;
    createdAt: Date;
}

interface Props {
    currentUser: User | undefined,
    locations: Array<Location> | undefined,
}

function UserLikes(props: Props) {
    const { id } = useParams<{ id: string }>();
    const [likes, setLikes] = useState<Like[]>([]);
    const [user, setUser] = useState<User | undefined>(undefined);
    const [loading, setLoading] = useState<boolean>(true);
    const isUiV2 = !!props.currentUser?.enableUiV2;

    const getLocationLink = (like: Like): string => {
        if (like.likeType === 'PHOTO') {
            return `/location/${like.locationId}/photos`;
        }
        if (like.likeType === 'COMMENT') {
            return `/location/${like.locationId}/comments?view=comments`;
        }
        return `/location/${like.locationId}/comments`;
    };

    const getEntityLink = (like: Like): string => {
        if (like.likeType === 'PHOTO') {
            return `/photo/${like.entityId}`;
        }
        if (like.likeType === 'COMMENT') {
            return `/location/${like.locationId}/comments?view=comments`;
        }
        return `/location/${like.locationId}/comments`;
    };

    const loadLikes = useCallback(async() => {
        if(!user || !id) {
            return;
        }

        try {
            setLoading(true);
            const likesQuery = query(
                collection(db, 'likes'),
                where('userId', '==', id),
                orderBy('createdAt', 'desc')
            );
            const likesSnapshot = await getDocs(likesQuery);
            const likesData: Like[] = [];
            likesSnapshot.forEach((docSnap) => {
                const data = docSnap.data();
                const like: Like = {
                    id: docSnap.id,
                    userId: data.userId,
                    userName: data.userName,
                    likeType: data.likeType,
                    entityId: data.entityId,
                    locationId: data.locationId,
                    locationName: data.locationName,
                    createdAt: data.createdAt,
                };
                likesData.push(like);
            });
            // Sort by createdAt in memory instead of in the query
            likesData.sort((a, b) => {
                const aTime = (a.createdAt as unknown as Timestamp).seconds || 0;
                const bTime = (b.createdAt as unknown as Timestamp).seconds || 0;
                return bTime - aTime;
            });
            console.log(`Loaded user likes: ${likesData.length} documents`);
            setLikes(likesData);
        } catch (error) {
            console.error("Error loading user likes:", error);
        } finally {
            setLoading(false);
        }
    }, [user, id]);

    useEffect(() => {
        if (!id) return;
        
        (async () => {
            const userRef = doc(db, 'users', id);
            const docSnap = await getDoc(userRef);
            if (docSnap.exists()) {
                let userToSet = docSnap.data() as User;
                userToSet.id = docSnap.id;
                setUser(userToSet);
            }
        })();
    }, [id]);

    useEffect(() => {
        if (!user) return;
        loadLikes();
    }, [user, loadLikes]);

    return (
        <>
            <PageTracking />
            {isUiV2 ? <UserDetailsV2 currentUser={props.currentUser} user={user} locations={props.locations}/> : <UserDetails user={user}/>}
            {!isUiV2 && <UserNav currentUser={props.currentUser} user={user}/>}
            {isUiV2 ? <V2GlassPanel className="user-v2-likes-list-card v2-glass-list-panel">
                {loading && <div className="user-v2-empty-list">Loading...</div>}
                {!loading && likes.length === 0 && <div className="user-v2-empty-list">No likes yet</div>}
                {!loading && likes.map((like) => (
                    <V2GlassSurface key={like.id} className="user-v2-like-row v2-glass-list-row">
                        <div className="user-v2-like-main">
                            <Link
                                to={getLocationLink(like)}
                                className="user-v2-like-location-link"
                            >
                                <V2GlassChip className="v2-glass-location-chip">{like.locationName}</V2GlassChip>
                            </Link>
                            <Link
                                className="user-v2-like-type-link"
                                to={getEntityLink(like)}
                            >
                                <V2GlassChip className="user-v2-like-type-chip">
                                    <FontAwesomeIcon icon={like.likeType === 'PHOTO' ? faCamera : like.likeType === 'LOCATION' ? faLocationDot : faComment} />
                                </V2GlassChip>
                            </Link>
                        </div>
                        <V2GlassChip className="user-v2-like-date-chip">
                            {getPhotoDate(like.createdAt)}
                        </V2GlassChip>
                    </V2GlassSurface>
                ))}
            </V2GlassPanel> : <Card className="mb-3">
                {!isUiV2 && likes.length > 0 && <Card.Header className={"header-padding"}>
                    <div className={"d-inline-block stats-card float-left"}>
                        Likes: <b>{likes.length}</b>
                    </div>
                </Card.Header>}
                <ListGroup variant="flush">
                    {loading && (
                        <div className={'loading'}>Loading...</div>
                    )}
                    {!loading && likes.length === 0 && (
                        <div className={'loading'}>No likes yet</div>
                    )}
                    {!loading && likes.map((like) => (
                        <div key={like.id} className={"news-item"}>
                            <div>
                                <div className={"d-inline-block mr-2"}>
                                    <Link 
                                        to={getLocationLink(like)}
                                        className={"p-0"}
                                    >
                                        <div className={`location-tag`}>{like.locationName}</div>
                                    </Link>
                                </div>
                                {like.likeType === 'PHOTO' && (
                                    <>
                                        <CameraTag />
                                        <div className={"d-inline-block ml-1 mr-1"}>
                                            <Link to={`/photo/${like.entityId}`}>
                                                <div className={`user-tag d-inline-block`}>
                                                    photo
                                                </div>
                                            </Link>
                                        </div>
                                    </>
                                )}
                                {like.likeType === 'COMMENT' && (
                                    <>
                                        <CommentTag />
                                        <div className={"d-inline-block ml-1 mr-1"}>
                                            <Link to={getEntityLink(like)}>
                                                <div className={`user-tag d-inline-block`}>
                                                    comment
                                                </div>
                                            </Link>
                                        </div>
                                    </>
                                )}
                                {like.likeType === 'LOCATION' && (
                                    <Link to={`/location/${like.locationId}/comments`}>
                                        <div className={`user-tag d-inline-block`}>
                                            <FontAwesomeIcon icon={faLocationDot}/>
                                        </div>
                                    </Link>
                                )}
                            </div>
                            <DateTime date={like.createdAt}/>
                        </div>
                    ))}
                </ListGroup>
            </Card>}
        </>
    );
}

export default UserLikes;
